import { queryOptions } from '@tanstack/react-query';

import { organizationHeader } from '@cast/constants';
import { Organization } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

import { DEFAULT_ORGANIZATION_NAME } from '../utils';

export const ORGANIZATIONS_QUERY_KEY = 'organizations';

export class OrganizationsFetchError extends Error {
  constructor(cause: unknown) {
    super('Failed to fetch organizations', { cause });
    this.name = 'OrganizationsFetchError';
  }
}

export const isOrganizationsFetchError = (
  error: unknown
): error is OrganizationsFetchError => error instanceof OrganizationsFetchError;

const makeOrganizations = (organizations: Organization[]) => {
  return organizations?.map((organization) => ({
    ...organization,
    name: organization.name || DEFAULT_ORGANIZATION_NAME,
  }));
};

const fetchOrganizations = async (
  initialOrganizationId?: string | null | undefined
) => {
  const headers = initialOrganizationId
    ? {
        [organizationHeader]: initialOrganizationId,
      }
    : undefined;

  const { data } = await apiClient.user.listOrganizations({
    headers,
  });
  return makeOrganizations((data?.organizations || []) as Organization[]);
};

const fetchOrganizationsWithFallback = async (
  initialOrganizationId?: string | null | undefined
) => {
  try {
    return await fetchOrganizations(initialOrganizationId);
  } catch (error) {
    if (!initialOrganizationId) {
      throw error;
    }
    return fetchOrganizations(null);
  }
};

export const getOrganizationsQueryOptions = ({
  initialOrganizationId,
}: {
  initialOrganizationId?: string | null | undefined;
} = {}) => {
  return queryOptions<Organization[]>({
    queryKey: [ORGANIZATIONS_QUERY_KEY],
    queryFn: async () => {
      try {
        return await fetchOrganizationsWithFallback(initialOrganizationId);
      } catch (error) {
        throw new OrganizationsFetchError(error);
      }
    },
    staleTime: 86_400_000,
  });
};
